import React, { useCallback, useEffect, useState } from 'react';
import { fetchAllRows } from '../lib/fetchAll';
import { SkeletonRows } from '../ui/Skeleton';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { ConfirmModal } from '@/components/ConfirmModal';
import { UserPostsModal } from '@/components/UserPostsModal';
import { UserFriendsModal } from '@/components/UserFriendsModal';
import { StampHoldersPanel } from '@/components/StampHoldersPanel';
import { ExportButton } from '../components/ExportButton';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';

type SortCol =
  | 'created_at'
  | 'username'
  | 'ratings_count'
  | 'friends_count'
  | 'followers_count'
  | 'city'
  | 'university'
  | 'is_private';
type SortDir = 'asc' | 'desc';

type UserRow = {
  id: string;
  username: string | null;
  full_name: string | null;
  created_at: string;
  is_admin: boolean | null;
  avatar_url?: string | null;
  is_private: boolean | null;
  city: string | null;
  university: string | null;
  ratings_count: number | string | null;
  friends_count: number | string | null;
  followers_count: number | string | null;
  total_count?: number | string | null;
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function rpcMissing(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('admin_list_users') || m.includes('42883') || m.includes('could not find the function');
}

export function UsersScreen({ canManageAdmins = false }: { canManageAdmins?: boolean }) {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortCol, setSortCol] = useState<SortCol>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [search, setSearch] = useState('');
  /**
   * How the user said they found Shado (`profiles.referral_source`). Filtered SERVER-SIDE via
   * `admin_list_users(p_referral_source)` — filtering the fetched page client-side would only ever
   * search the rows already on screen, and this list is paginated in the RPC.
   */
  const [referral, setReferral] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [banTarget, setBanTarget] = useState<{ id: string; username: string | null } | null>(null);
  const [adminToggleTarget, setAdminToggleTarget] = useState<UserRow | null>(null);
  const [postsUser, setPostsUser] = useState<UserRow | null>(null);
  const [friendsUser, setFriendsUser] = useState<UserRow | null>(null);
  /** Signup ordinal (#1 = first account ever) keyed by user id — survives any sort/filter/page. */
  const [joinOrder, setJoinOrder] = useState<Record<string, number>>({});
  const { showError, showSuccess, toastNode } = useToast();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const all = await fetchAllRows<{ id: string; created_at: string }>((from, to) =>
          supabase.from('profiles').select('id, created_at').range(from, to),
        );
        if (cancelled) return;
        const map: Record<string, number> = {};
        [...all]
          .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
          .forEach((p, i) => {
            map[p.id] = i + 1;
          });
        setJoinOrder(map);
      } catch {
        // Non-fatal: rows just show the date without an ordinal.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_users', {
        p_limit: pageSize,
        p_offset: page * pageSize,
        p_search: debouncedSearch || null,
        p_sort: sortCol,
        p_dir: sortDir,
        p_referral_source: referral || null,
      });
      if (rpcErr) {
        if (rpcMissing(rpcErr.message)) {
          throw new Error('Users list is not enabled yet — run `supabase db push` to apply migration 20260764000000.');
        }
        throw rpcErr;
      }
      const list = (data ?? []) as UserRow[];
      setRows(list);
      setTotal(list.length > 0 ? num(list[0].total_count) : 0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortCol, sortDir, referral]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, pageSize, sortCol, sortDir, referral]);

  function toggleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  async function applyAdminToggle() {
    const p = adminToggleTarget;
    if (!p || !canManageAdmins) return;
    setBusyId(p.id);
    try {
      const next = !p.is_admin;
      const { error: upErr } = await supabase.rpc('admin_set_user_admin', { p_target: p.id, p_is_admin: next });
      if (upErr) throw upErr;
      setRows((prev) => prev.map((r) => (r.id === p.id ? { ...r, is_admin: next } : r)));
      setAdminToggleTarget(null);
      showSuccess(
        next ? `Granted admin access to @${p.username ?? p.id}` : `Revoked admin access from @${p.username ?? p.id}`,
      );
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmBan() {
    if (!banTarget) return;
    const { id, username } = banTarget;
    setBusyId(id);
    setError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_ban_user', {
        p_target: id,
        p_reason: null,
      });
      if (rpcErr) throw rpcErr;
      setRows((prev) => prev.filter((r) => r.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      setBanTarget(null);
      showSuccess(`Banned @${username ?? id}, phone blocked`);
    } catch (e: unknown) {
      const detail = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Unknown error';
      setError(`Could not ban user: ${detail}`);
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setBusyId(targetId);
    setError(null);
    try {
      type FnBody = { ok?: boolean; error?: string; hint?: string };
      const { data, error: fnError } = await supabase.functions.invoke<FnBody>('admin-delete-user', {
        body: { target_user_id: targetId },
      });
      if (fnError) {
        let msg = fnError.message?.trim() || '';
        const ctx = (fnError as { context?: { json: () => Promise<unknown> } }).context;
        if (ctx?.json) {
          try {
            const parsed = (await ctx.json()) as FnBody;
            if (parsed?.error) msg = parsed.hint ? `${parsed.error} — ${parsed.hint}` : parsed.error;
          } catch {
            /* keep msg */
          }
        }
        if (!msg || msg === 'Edge Function returned a non-2xx status code') {
          msg =
            'The delete service did not succeed. Confirm the admin-delete-user Edge Function is deployed and you are signed in as an admin.';
        }
        throw new Error(msg);
      }
      if (data && typeof data === 'object' && 'error' in data && data.error) {
        const hint = 'hint' in data && data.hint ? ` — ${data.hint}` : '';
        throw new Error(`${data.error}${hint}`);
      }
      setRows((prev) => prev.filter((r) => r.id !== targetId));
      setTotal((t) => Math.max(0, t - 1));
      setDeleteTarget(null);
      showSuccess('User deleted');
    } catch (e: unknown) {
      const detail = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Unknown error';
      setError(`Could not delete user: ${detail}`);
    } finally {
      setBusyId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const SortHeader = ({ label, col, right }: { label: string; col: SortCol; right?: boolean }) => {
    const active = sortCol === col;
    return (
      <th
        scope="col"
        className={`sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5 ${right ? 'text-right' : ''}`}
      >
        <button
          type="button"
          onClick={() => toggleSort(col)}
          className={`inline-flex items-center gap-1 transition-colors hover:text-white ${active ? 'text-white' : ''}`}
        >
          {label}
          {active ? (
            <span className="text-accent">{sortDir === 'asc' ? '↑' : '↓'}</span>
          ) : (
            <span className="text-mutedDim opacity-60" aria-hidden>
              ↕
            </span>
          )}
        </button>
      </th>
    );
  };

  return (
    <div className="admin-page max-w-7xl mx-auto">
      <PageHeader title="Users" subtitle="Search, inspect, and manage users. Click a username to see their posts." />
      {!canManageAdmins ? (
        <p className="mb-5 text-xs text-mutedDim leading-relaxed max-w-2xl">
          Admin access can only be granted or revoked by @evan. You can view roles but not change them.
        </p>
      ) : null}

      {/*
        Browse by stamp. Collapsed until asked for — it reads the whole rating corpus to compute the
        rosters, and the paginated table below must not pay for that on every page change. Sits above
        the table rather than beside it because it answers a different question (which PEOPLE hold a
        stamp) than the table's filters do (which people match a string).
      */}
      <StampHoldersPanel />

      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <input
          type="search"
          placeholder="Search username or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-input admin-input-lg !w-auto min-w-[220px]"
        />
        {/*
          Found-via filter. Values are the fixed set `profiles.referral_source` actually holds —
          verified on prod 2026-08-18: friend 370, instagram 51, family 33, other 24, tiktok 1.
          Hard-coded rather than fetched: it is a signup enum, not user input, and one round trip to
          discover five known strings is not worth it.
        */}
        <label className="flex items-center gap-2 text-xs text-muted">
          <span className="text-mutedDim">Found via</span>
          <select
            value={referral}
            onChange={(e) => setReferral(e.target.value)}
            className="admin-input !w-auto !px-2 !text-xs"
          >
            <option value="">All</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="friend">Friend</option>
            <option value="family">Family</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-muted">
          <span className="text-mutedDim">Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="admin-input !w-auto !px-2 !text-xs"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <ExportButton
          rows={rows}
          columns={[
            { header: 'username', get: (r: UserRow) => r.username ?? '' },
            { header: 'full_name', get: (r: UserRow) => r.full_name ?? '' },
            { header: 'city', get: (r: UserRow) => r.city ?? '' },
            { header: 'university', get: (r: UserRow) => r.university ?? '' },
            { header: 'ratings_count', get: (r: UserRow) => num(r.ratings_count) },
            { header: 'friends_count', get: (r: UserRow) => num(r.friends_count) },
            { header: 'followers_count', get: (r: UserRow) => num(r.followers_count) },
            { header: 'is_private', get: (r: UserRow) => Boolean(r.is_private) },
            { header: 'is_admin', get: (r: UserRow) => Boolean(r.is_admin) },
            { header: 'created_at', get: (r: UserRow) => r.created_at },
          ]}
          filename="users"
        />
        <span className="text-[11px] uppercase tracking-wider tabular-nums text-mutedDim">
          Page {page + 1} / {totalPages} · {total.toLocaleString()} users
        </span>
      </div>

      {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}

      {loading ? (
        <SkeletonRows rows={9} className="py-4" />
      ) : (
        <div className="rounded-xl border border-border bg-surface overflow-x-auto">
          <table className="w-full border-collapse text-sm min-w-[980px]">
            <thead>
              <tr className="admin-table-head">
                <th
                  scope="col"
                  className="sticky top-0 z-10 w-10 border-b border-border/80 bg-surface px-3 py-3.5 text-right"
                >
                  #
                </th>
                <th scope="col" className="sticky top-0 z-10 w-12 border-b border-border/80 bg-surface px-4 py-3.5" aria-label="Avatar" />
                <SortHeader label="Username" col="username" />
                <SortHeader label="Joined" col="created_at" />
                <SortHeader label="Ratings" col="ratings_count" right />
                <SortHeader label="Friends" col="friends_count" right />
                <SortHeader label="Followers" col="followers_count" right />
                <SortHeader label="Privacy" col="is_private" />
                <SortHeader label="City" col="city" />
                <SortHeader label="University" col="university" />
                <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">Admin</th>
                <th scope="col" className="sticky top-0 z-10 border-b border-border/80 bg-surface px-4 py-3.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isPrivate = Boolean(r.is_private);
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-border/50 transition-colors hover:bg-white/[0.03] ${
                      i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'
                    }`}
                  >
                    {/* Global rank under the currently-active sort. admin_list_users sorts the FULL
                        result set server-side then applies LIMIT/OFFSET, so page*pageSize + row index
                        is already the true global position — no client-side re-sort needed. Ties keep
                        whatever stable order the RPC's `ORDER BY sub.<col> <dir> NULLS LAST, sub.id <dir>`
                        tiebreaker produces. */}
                    <td className="px-3 py-4 text-right font-mono text-2xs tabular-nums text-mutedDim">
                      {(page * pageSize + i + 1).toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="h-9 w-9 rounded-full bg-border overflow-hidden flex items-center justify-center text-xs text-muted">
                        {r.avatar_url ? (
                          <img src={r.avatar_url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
                        ) : (
                          (r.username ?? '?').slice(0, 1).toUpperCase()
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => setPostsUser(r)}
                        className="admin-label-primary text-left underline-offset-2 hover:text-accent hover:underline"
                      >
                        {r.username ?? '—'}
                      </button>
                      {r.full_name?.trim() ? (
                        <div className="text-xs text-mutedDim mt-0.5 max-w-[160px] truncate">{r.full_name}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 text-mutedDim whitespace-nowrap text-sm tabular-nums">
                      {joinOrder[r.id] != null ? (
                        <span className="mr-1.5 font-semibold text-accent/90" title={`User #${joinOrder[r.id]} to join`}>
                          #{joinOrder[r.id]}
                        </span>
                      ) : null}
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right admin-metric tabular-nums">{num(r.ratings_count).toLocaleString()}</td>
                    <td className="px-4 py-4 text-right tabular-nums">
                      <button
                        type="button"
                        onClick={() => setFriendsUser(r)}
                        className="admin-metric underline-offset-2 hover:text-accent hover:underline"
                        title="View friends (mutual follows)"
                      >
                        {num(r.friends_count).toLocaleString()}
                      </button>
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums">
                      {isPrivate ? (
                        <span className="text-mutedDim">—</span>
                      ) : (
                        <span className="admin-metric">{num(r.followers_count).toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`admin-status ${isPrivate ? 'admin-status-warning' : 'admin-status-success'}`}>
                        {isPrivate ? 'Private' : 'Public'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-muted text-xs max-w-[160px] truncate">{r.city?.trim() || '—'}</td>
                    <td className="px-4 py-4 text-muted text-xs max-w-[160px] truncate">{r.university?.trim() || '—'}</td>
                    <td className="px-4 py-4">
                      {canManageAdmins ? (
                        <label className="inline-flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(r.is_admin)}
                            disabled={busyId === r.id}
                            onChange={() => setAdminToggleTarget(r)}
                            className="h-4 w-4 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
                          />
                        </label>
                      ) : (
                        <span className={`admin-status ${r.is_admin ? 'admin-status-accent' : 'admin-status-neutral'}`}>
                          {r.is_admin ? 'Yes' : 'No'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setPostsUser(r)}
                          className="text-xs font-semibold text-violet-300/95 hover:text-violet-200"
                        >
                          Posts
                        </button>
                        <button
                          type="button"
                          onClick={() => setFriendsUser(r)}
                          className="text-xs font-semibold text-violet-300/95 hover:text-violet-200"
                        >
                          Friends
                        </button>
                        <button
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => setBanTarget({ id: r.id, username: r.username })}
                          className="text-xs font-semibold text-amber-400/95 hover:text-amber-300 disabled:opacity-50"
                        >
                          Ban
                        </button>
                        <button
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => setDeleteTarget(r)}
                          className="text-xs font-semibold text-red-400/95 hover:text-red-300 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-muted text-sm">No users</div>
          ) : null}
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <button
          type="button"
          disabled={page <= 0 || loading}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="admin-btn admin-btn-secondary disabled:opacity-40 transition-colors"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={page >= totalPages - 1 || loading}
          onClick={() => setPage((p) => p + 1)}
          className="admin-btn admin-btn-secondary disabled:opacity-40 transition-colors"
        >
          Next
        </button>
      </div>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete user"
        message={
          deleteTarget
            ? `Permanently delete @${deleteTarget.username ?? deleteTarget.id}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        busy={Boolean(deleteTarget) && busyId === deleteTarget?.id}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmModal
        open={Boolean(banTarget)}
        title="Ban user"
        message={
          banTarget
            ? `Ban @${banTarget.username ?? banTarget.id}? This suspends the account AND blocks their phone number from signing up again.`
            : ''
        }
        confirmLabel="Ban"
        cancelLabel="Cancel"
        danger
        busy={Boolean(banTarget) && busyId === banTarget?.id}
        onConfirm={() => void confirmBan()}
        onCancel={() => setBanTarget(null)}
      />

      <ConfirmModal
        open={Boolean(adminToggleTarget)}
        title="Admin access"
        message={
          adminToggleTarget
            ? `Are you sure you want to ${
                adminToggleTarget.is_admin ? 'revoke' : 'grant'
              } admin access for ${adminToggleTarget.username ?? adminToggleTarget.id}?`
            : ''
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        busy={Boolean(adminToggleTarget) && busyId === adminToggleTarget?.id}
        onConfirm={() => void applyAdminToggle()}
        onCancel={() => setAdminToggleTarget(null)}
      />

      <UserPostsModal user={postsUser} onClose={() => setPostsUser(null)} />
      <UserFriendsModal user={friendsUser} onClose={() => setFriendsUser(null)} />

      {toastNode}
    </div>
  );
}
